import { useState } from "react";

const SPOTS = {
  tap: {
    label: "Kitchen tap",
    text: "Water samples collected here and sent to the laboratory. On-site screening readings where available.",
    cx: 118,
    cy: 112,
  },
  stove: {
    label: "Stove",
    text: "Emissions readings taken while burners are in use. Applies to gas stoves.",
    cx: 190,
    cy: 112,
  },
  bedroom: {
    label: "Bedroom",
    text: "Seven-day air monitor placed here, recording fine particles, CO₂, total VOCs, humidity and temperature.",
    cx: 360,
    cy: 118,
  },
};

const Hotspot = ({ id, spot, active, onSelect }) => (
  <g
    role="button"
    tabIndex={0}
    aria-pressed={active}
    aria-label={spot.label}
    data-testid={`apartment-hotspot-${id}`}
    onClick={() => onSelect(id)}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(id))}
    className="cursor-pointer outline-none focus-visible:[&>circle:first-child]:stroke-ink"
  >
    <circle cx={spot.cx} cy={spot.cy} r="30" fill="transparent" stroke="transparent" strokeWidth="2" />
    {!active && <circle className="hotspot-ring" cx={spot.cx} cy={spot.cy} r="9" fill="#A8DAD8" style={{ transformBox: "fill-box" }} />}
    <circle cx={spot.cx} cy={spot.cy} r="9" fill={active ? "#163B30" : "#FFFFFF"} stroke="#163B30" strokeWidth="2" />
    <circle cx={spot.cx} cy={spot.cy} r="3" fill={active ? "#FFFFFF" : "#163B30"} />
  </g>
);

const LABEL_STYLE = { fontSize: 11, fill: "#52665E", fillOpacity: 1, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: 1.5 };
const Label = ({ x, y, children }) => (
  <text x={x} y={y} fontSize={LABEL_STYLE.fontSize} fill={LABEL_STYLE.fill} fillOpacity={LABEL_STYLE.fillOpacity} fontFamily={LABEL_STYLE.fontFamily} letterSpacing={LABEL_STYLE.letterSpacing}>
    {children}
  </text>
);

const FloorPlan = ({ active }) => {
  const kitchenOn = active === "tap" || active === "stove";
  return (
    <>
      <rect x="20" y="20" width="400" height="260" rx="4" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.25" strokeWidth="2" />
      <rect x="20" y="20" width="220" height="130" fill={kitchenOn ? "#EAF4EF" : "#FFFFFF"} stroke="#163B30" strokeOpacity="0.18" />
      <Label x="34" y="44">KITCHEN</Label>
      <rect x="60" y="96" width="160" height="34" rx="2" fill="#EAF4EF" stroke="#163B30" strokeOpacity="0.2" />
      <rect x="98" y="102" width="40" height="22" rx="4" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.35" />
      {[[180, 108], [200, 108], [180, 122], [200, 122]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4" fill="none" stroke="#163B30" strokeOpacity="0.4" />
      ))}
      <rect x="20" y="150" width="220" height="130" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.18" />
      <Label x="34" y="174">LIVING</Label>
      <rect x="60" y="210" width="110" height="36" rx="6" fill="#EAF4EF" stroke="#163B30" strokeOpacity="0.2" />
      <rect x="60" y="277" width="60" height="4" fill="#A8DAD8" />
      <rect x="150" y="277" width="60" height="4" fill="#A8DAD8" />
      <rect x="300" y="277" width="80" height="4" fill="#A8DAD8" />
      <rect x="240" y="20" width="180" height="180" fill={active === "bedroom" ? "#EAF4EF" : "#FFFFFF"} stroke="#163B30" strokeOpacity="0.18" />
      <Label x="254" y="44">BEDROOM</Label>
      <rect x="280" y="80" width="100" height="90" rx="4" fill="#EAF4EF" stroke="#163B30" strokeOpacity="0.2" />
      <rect x="288" y="86" width="36" height="18" rx="3" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.2" />
      <rect x="336" y="86" width="36" height="18" rx="3" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.2" />
      <rect x="392" y="96" width="14" height="14" rx="2" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.3" />
      <rect x="240" y="200" width="180" height="80" fill="#FFFFFF" stroke="#163B30" strokeOpacity="0.18" />
      <Label x="254" y="224">BATH</Label>
      <rect x="330" y="236" width="70" height="30" rx="8" fill="#EAF4EF" stroke="#163B30" strokeOpacity="0.2" />
    </>
  );
};

const SpotTabs = ({ active, onSelect }) => (
  <div className="mt-3 flex gap-2 flex-wrap">
    {Object.entries(SPOTS).map(([id, s]) => (
      <button
        key={id}
        type="button"
        onClick={() => onSelect(id)}
        aria-pressed={active === id}
        data-testid={`apartment-tab-${id}`}
        className={`chip !min-h-[44px] sm:!min-h-[36px] !py-1.5 !px-3 text-xs ${active === id ? "chip-active" : "chip-idle"}`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

export const ApartmentMap = ({ selected, onSelect }) => {
  const [internal, setInternal] = useState("bedroom");
  const active = selected ?? internal;
  const choose = onSelect ?? setInternal;
  const spot = SPOTS[active];

  return (
    <div className="rounded-xl border border-divider bg-white p-4 sm:p-6" data-testid="apartment-map">
      <p className="eyebrow mb-3">Where we work in your home</p>
      <svg viewBox="0 0 440 300" className="w-full h-auto" role="img" aria-label="Simplified apartment floor plan with kitchen, living room, bedroom and bath">
        <FloorPlan active={active} />
        {Object.entries(SPOTS).map(([id, s]) => (
          <Hotspot key={id} id={id} spot={s} active={active === id} onSelect={choose} />
        ))}
      </svg>
      <div className="mt-4 border-t border-divider pt-4 min-h-[76px]" aria-live="polite" data-testid="apartment-map-caption">
        <p className="text-sm font-semibold text-ink">{spot.label}</p>
        <p className="mt-1 text-sm leading-relaxed text-sec">{spot.text}</p>
      </div>
      <SpotTabs active={active} onSelect={choose} />
    </div>
  );
};
